import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DEVICES = {
  iphone: {
    name: 'iPhone 16 Pro',
    screenW: 393,
    screenH: 852,
    bezelTop: 14,
    bezelBottom: 14,
    bezelSide: 10,
    outerRadius: 54,
    innerRadius: 46,
    bodyColor: 'linear-gradient(145deg, #E8E8EC 0%, #F4F4F6 15%, #D0D0D6 40%, #E0E0E5 55%, #C8C8D0 75%, #DCDCE2 100%)',
    buttonColor: '#B8B8C2',
  },
  samsung: {
    name: 'Galaxy S25 Ultra',
    screenW: 400,
    screenH: 868,
    bezelTop: 10,
    bezelBottom: 10,
    bezelSide: 6,
    outerRadius: 36,
    innerRadius: 32,
    bodyColor: 'linear-gradient(145deg, #E5E7EB 0%, #F3F4F6 15%, #CFD1D8 40%, #DEE0E5 55%, #C6C9D0 75%, #DBDDE3 100%)',
    buttonColor: '#B0B4BC',
  },
};

const IPhoneNotch = () => (
  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 pointer-events-none" style={{ top: 10 }}>
    <div
      className="flex items-center justify-center"
      style={{
        width: 126,
        height: 36,
        borderRadius: 20,
        background: '#1a1a1e',
      }}
    >
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0a0a0e', border: '2px solid #2a2a3e' }} />
    </div>
  </div>
);

const SamsungCamera = () => (
  <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 pointer-events-none" style={{ top: 12 }}>
    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#1a1a1e', border: '2.5px solid #2a2a3e' }} />
  </div>
);

const StatusBar = ({ device }: { device: string }) => {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return (
    <div
      className="absolute top-0 left-0 right-0 z-20 flex items-end justify-between px-6 pointer-events-none select-none"
      style={{
        height: device === 'iphone' ? 54 : 40,
        paddingBottom: 4,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
      }}
    >
      <span className="text-white font-semibold" style={{ fontSize: 14 }}>{timeStr}</span>
      <div className="flex items-center gap-1">
        <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
          <rect x="0" y="8" width="3" height="4" rx="0.5" fill="white"/>
          <rect x="4.5" y="5" width="3" height="7" rx="0.5" fill="white"/>
          <rect x="9" y="2" width="3" height="10" rx="0.5" fill="white"/>
          <rect x="13.5" y="0" width="2.5" height="12" rx="0.5" fill="white" opacity="0.3"/>
        </svg>
        <svg width="14" height="12" viewBox="0 0 14 12" fill="none">
          <path d="M7 10.5a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4z" fill="white"/>
          <path d="M3.5 7.2a5 5 0 017 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
          <path d="M1.2 4.5a8.3 8.3 0 0111.6 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
        </svg>
        <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
          <rect x="0.5" y="1" width="20" height="10" rx="2" stroke="white" strokeWidth="1"/>
          <rect x="21" y="3.5" width="2.5" height="5" rx="1" fill="white" opacity="0.4"/>
          <rect x="2" y="3" width="14" height="6" rx="1" fill="#34C759"/>
        </svg>
      </div>
    </div>
  );
};

const HomeIndicator = ({ device }: { device: string }) => (
  <div className="absolute bottom-0 left-0 right-0 z-20 flex justify-center pointer-events-none" style={{ paddingBottom: device === 'iphone' ? 8 : 6 }}>
    <div style={{ width: 134, height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.2)' }} />
  </div>
);

export default function DeviceFrame({ children }: { children: React.ReactNode }) {
  const [device, setDevice] = useState(() => localStorage.getItem('dd_device') || 'iphone');
  const [isDesktop, setIsDesktop] = useState(false);
  const [scale, setScale] = useState(1);
  const location = useLocation();

  // Pages that render WITHOUT the phone frame (full desktop layout)
  const noFrame = location.pathname === '/' || location.pathname === '/landing' || location.pathname.startsWith('/admin') || location.pathname === '/about' || location.pathname === '/mission' || location.pathname === '/vision' || location.pathname === '/safety-info' || location.pathname === '/faq' || location.pathname === '/privacy' || location.pathname === '/terms' || location.pathname === '/blog' || location.pathname.startsWith('/blog/') || location.pathname === '/pricing' || location.pathname.startsWith('/pricing/');

  useEffect(() => {
    const check = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
      if (desktop) {
        const d = DEVICES[device as keyof typeof DEVICES];
        const totalH = d.screenH + d.bezelTop + d.bezelBottom;
        const maxH = window.innerHeight - 80;
        const maxW = window.innerWidth - 80;
        const totalW = d.screenW + d.bezelSide * 2;
        const s = Math.min(1, maxH / totalH, maxW / totalW);
        setScale(s);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [device]);

  useEffect(() => {
    localStorage.setItem('dd_device', device);
  }, [device]);

  if (!isDesktop || noFrame) return <>{children}</>;

  const d = DEVICES[device as keyof typeof DEVICES];
  const totalW = d.screenW + d.bezelSide * 2;
  const totalH = d.screenH + d.bezelTop + d.bezelBottom;

  return (
    <div
      data-testid="device-frame-wrapper"
      className="fixed inset-0 flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f0f12 0%, #1a1a2e 40%, #16213e 70%, #0f0f12 100%)',
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: totalW + 160,
          height: totalH + 160,
          borderRadius: d.outerRadius + 50,
          background: 'radial-gradient(ellipse, rgba(200,200,210,0.12) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Device toggle */}
      <div
        data-testid="device-toggle"
        className="absolute z-50 flex items-center gap-1 p-1 rounded-full"
        style={{
          top: 20,
          right: 20,
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <button
          data-testid="device-toggle-iphone"
          onClick={() => setDevice('iphone')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
          style={{
            background: device === 'iphone' ? 'rgba(255,255,255,0.12)' : 'transparent',
            color: device === 'iphone' ? '#fff' : 'rgba(255,255,255,0.45)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="3"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>
          iPhone 16 Pro
        </button>
        <button
          data-testid="device-toggle-samsung"
          onClick={() => setDevice('samsung')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
          style={{
            background: device === 'samsung' ? 'rgba(255,255,255,0.12)' : 'transparent',
            color: device === 'samsung' ? '#fff' : 'rgba(255,255,255,0.45)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="4" y="2" width="16" height="20" rx="2"/><circle cx="12" cy="5" r="1"/></svg>
          Galaxy S25 Ultra
        </button>
      </div>

      {/* Device label */}
      <div className="absolute z-50 text-center" style={{ bottom: 24 }}>
        <p className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {d.name} &middot; Silver
        </p>
      </div>

      {/* Phone body - scaled to fit viewport */}
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        <div
          data-testid="device-frame-body"
          className="relative"
          style={{
            width: totalW,
            height: totalH,
            borderRadius: d.outerRadius,
            background: d.bodyColor,
            boxShadow: `
              0 0 0 0.5px rgba(255,255,255,0.8),
              0 0 0 1.5px rgba(180,180,190,0.6),
              0 0 0 2.5px rgba(255,255,255,0.3),
              inset 0 1px 2px rgba(255,255,255,0.9),
              inset 0 -1px 1px rgba(0,0,0,0.06),
              0 30px 80px rgba(0,0,0,0.45),
              0 10px 30px rgba(0,0,0,0.25)
            `,
          }}
        >
          {/* Side buttons - iPhone (silver) */}
          {device === 'iphone' && (
            <>
              <div className="absolute" style={{ left: -3, top: 100, width: 3, height: 28, borderRadius: '3px 0 0 3px', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
              <div className="absolute" style={{ left: -3, top: 145, width: 3, height: 52, borderRadius: '3px 0 0 3px', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
              <div className="absolute" style={{ left: -3, top: 205, width: 3, height: 52, borderRadius: '3px 0 0 3px', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
              <div className="absolute" style={{ right: -3, top: 170, width: 3, height: 72, borderRadius: '0 3px 3px 0', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
            </>
          )}
          {/* Side buttons - Samsung (chrome) */}
          {device === 'samsung' && (
            <>
              <div className="absolute" style={{ right: -2.5, top: 160, width: 2.5, height: 55, borderRadius: '0 3px 3px 0', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
              <div className="absolute" style={{ right: -2.5, top: 230, width: 2.5, height: 38, borderRadius: '0 3px 3px 0', background: 'linear-gradient(180deg, #E0E0E6 0%, #C8C8D0 50%, #E0E0E6 100%)' }} />
            </>
          )}

          {/* Screen area */}
          <div
            data-testid="device-screen"
            className="absolute overflow-hidden"
            style={{
              top: d.bezelTop,
              left: d.bezelSide,
              width: d.screenW,
              height: d.screenH,
              borderRadius: d.innerRadius,
              background: '#000',
              contain: 'layout paint',
            }}
          >
            <StatusBar device={device} />
            {device === 'iphone' ? <IPhoneNotch /> : <SamsungCamera />}
            <HomeIndicator device={device} />
            <div
              data-testid="device-screen-content"
              className="w-full h-full overflow-y-auto overflow-x-hidden"
              style={{ WebkitOverflowScrolling: 'touch', paddingTop: device === 'iphone' ? 54 : 40 }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
